import { Controller } from '@hotwired/stimulus'
import { currentLocation, renderWalkResponse, sendJSON } from '../lib/prayer_walk'

export default class extends Controller {
  static targets = ['elapsed', 'finish', 'status']
  static values = { startedAt: String, storageKey: String, locationsUrl: String, finishUrl: String }

  connect () {
    this.connected = true
    this.state = this.readState()
    this.tick()
    this.clock = window.setInterval(() => this.tick(), 1000)
    this.sampler = window.setInterval(() => this.sample(), 60000)
    this.onVisible = () => {
      this.tick()
      if (document.visibilityState === 'visible') this.sample()
    }
    this.onOnline = () => this.state.finish ? this.saveFinish() : this.upload()
    document.addEventListener('visibilitychange', this.onVisible)
    window.addEventListener('online', this.onOnline)
    if (this.state.finish) {
      this.finishTarget.textContent = 'Retry saving finished walk'
      this.saveFinish()
    } else {
      this.sample()
    }
  }

  disconnect () {
    this.connected = false
    window.clearInterval(this.clock)
    window.clearInterval(this.sampler)
    document.removeEventListener('visibilitychange', this.onVisible)
    window.removeEventListener('online', this.onOnline)
  }

  async finish () {
    if (this.saving || this.gettingEndLocation) return
    if (!this.state.finish) {
      this.gettingEndLocation = true
      this.state.finish = { ended_at: new Date().toISOString(), end_location: null }
      this.persist()
      this.tick()
      this.finishTarget.disabled = true
      this.statusTarget.textContent = 'Getting your finishing location…'
      this.state.finish.end_location = await currentLocation()
      this.gettingEndLocation = false
      this.persist()
    }
    await this.saveFinish()
  }

  async sample () {
    if (!this.connected || this.state.finish || this.sampling || document.visibilityState !== 'visible') return
    if (Date.now() - (this.state.lastSampleAt || 0) < 60000) {
      await this.upload()
      return
    }
    this.sampling = true
    const location = await currentLocation()
    this.sampling = false
    if (!this.connected || this.state.finish) return
    if (location) {
      this.state.samples.push(location)
      this.state.samples = this.state.samples.slice(-1440)
      this.state.lastSampleAt = Date.now()
      this.persist()
      await this.upload()
    } else {
      this.statusTarget.textContent = 'GPS unavailable or permission denied. The timer still works; no location will be invented.'
    }
  }

  async saveFinish () {
    if (this.saving || !this.state.finish) return
    this.saving = true
    this.finishTarget.disabled = true
    try {
      if (this.uploadPromise) await this.uploadPromise
      this.statusTarget.textContent = 'Saving your walk…'
      const result = await sendJSON(this.finishUrlValue, 'PATCH', {
        prayer_walk: { ...this.state.finish, route_samples: this.state.samples }
      })
      try {
        window.localStorage.removeItem(this.storageKeyValue)
      } catch (_) {}
      renderWalkResponse(result)
    } catch (error) {
      const recovery = this.deviceStorageAvailable === false
        ? 'Keep this page open; device storage is unavailable.'
        : 'Your finish time is saved on this device.'
      this.statusTarget.textContent = `${error.message} ${recovery} Tap retry when online.`
      this.finishTarget.textContent = 'Retry saving finished walk'
    } finally {
      this.saving = false
      this.finishTarget.disabled = false
    }
  }

  tick () {
    const end = this.state.finish ? Date.parse(this.state.finish.ended_at) : Date.now()
    const seconds = Math.max(0, Math.floor((end - Date.parse(this.startedAtValue)) / 1000))
    this.elapsedTarget.textContent = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
  }

  readState () {
    try {
      const saved = JSON.parse(window.localStorage.getItem(this.storageKeyValue))
      if (saved && Array.isArray(saved.samples)) return saved
    } catch (_) {}
    return { samples: [], finish: null, lastSampleAt: 0 }
  }

  persist () {
    try {
      window.localStorage.setItem(this.storageKeyValue, JSON.stringify(this.state))
      this.deviceStorageAvailable = true
    } catch (_) {
      this.deviceStorageAvailable = false
      this.statusTarget.textContent = 'Device storage is unavailable. Keep this page open until your walk is saved.'
    }
  }

  async upload () {
    if (this.uploadPromise || !this.state.samples.length || this.state.finish) return
    const samples = [...this.state.samples]
    this.uploadPromise = (async () => {
      try {
        await sendJSON(this.locationsUrlValue, 'POST', { route_samples: samples })
        const sent = new Set(samples.map(sample => sample.recorded_at))
        this.state.samples = this.state.samples.filter(sample => !sent.has(sample.recorded_at))
        this.persist()
        this.statusTarget.textContent = 'Location samples saved.'
      } catch (error) {
        const recovery = this.deviceStorageAvailable === false
          ? 'Keep this page open to retry; device storage is unavailable.'
          : 'GPS samples are kept on this device for retry.'
        this.statusTarget.textContent = `${error.message} ${recovery}`
      }
    })()
    await this.uploadPromise
    this.uploadPromise = null
  }
}
